import { useCallback, useEffect, useState } from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import type { INotificationResponseDto } from "Common/Interfaces";
import { getAccessToken } from "../../Store/AuthenticationHelper";
import { getTimeZone } from "../../Store/Functions/GetTimeZone";

const hubUrl = new URL("signalr", import.meta.env.VITE_MESSAGE_CENTER_API_BASE_URL).href;

export function useMessageCenterSignalR() {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);

  useEffect(() => {
    if (!hubUrl) return;

    let cancelled = false;

    const buildConnection = async () => {
      const timeZone = await getTimeZone();

      const hubConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            const token = await getAccessToken();
            return token ?? "";
          },
          headers: {
            "Ocp-Apim-Subscription-Key": import.meta.env.VITE_SUBSCRIPTION_KEY ?? "",
            "X-timeZone": timeZone,
          },
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect()
        .build();

      if (!cancelled) setConnection(hubConnection);
    };

    buildConnection();

    return () => {
      cancelled = true;
    };
  }, []);

  const startConnection = useCallback(async () => {
    if (connection?.state === HubConnectionState.Disconnected) {
      try {
        setConnectionState(HubConnectionState.Connecting);
        await connection.start();
        setConnectionState(HubConnectionState.Connected);
      } catch (err) {
        setConnectionState(HubConnectionState.Disconnected);
        console.error("Error starting message center connection:", err);
      }
    }
  }, [connection]);

  const stopConnection = useCallback(async () => {
    if (connection?.state !== HubConnectionState.Disconnected) {
      try {
        await connection?.stop();
        setConnectionState(HubConnectionState.Disconnected);
      } catch (err) {
        console.error("Error stopping message center connection:", err);
      }
    }
  }, [connection]);

  const setNotificationCallback = useCallback((callback: (notification: INotificationResponseDto) => void): void => {
    if (connection) {
      connection.off("ReceiveNotification");
      connection.on("ReceiveNotification", callback);
    }
  }, [connection]);

  const isConnected = useCallback((): boolean => {
    return connectionState === HubConnectionState.Connected;
  }, [connectionState]);

  return {
    connection,
    connectionState,
    startConnection,
    stopConnection,
    setNotificationCallback,
    isConnected,
  };
}
